/**
 * A multi-select that behaves like a select: a field-shaped button showing
 * what is chosen, and a list that opens OVER the page rather than pushing it
 * around. The pattern every administrative product uses for "which groups
 * does this person belong to" (Okta, Google Workspace, Slack, Atlassian):
 * type to filter, tick what applies, Done.
 *
 * The list is rendered in a portal and positioned from the button's
 * rectangle, so a table cell's scroll container cannot clip it and nothing
 * below the button moves. It flips above the button when the viewport has
 * more room there. Escape or a click elsewhere closes it and returns focus
 * to the button; arrow keys walk the options.
 */
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { FILTER_MAX } from '../types/fieldLimits';
import styles from './MultiSelect.module.css';

export interface MultiSelectOption {
  id: string;
  name: string;
  /** A second line under the name: what the role does, where the unit is. */
  hint?: string;
}

export interface MultiSelectProps {
  id: string;
  /** The accessible name of the control: "Departments", "Roles for Dana Reyes". */
  label: string;
  options: readonly MultiSelectOption[];
  value: readonly string[];
  onChange: (next: string[]) => void;
  /** Shown in the field when nothing is chosen. */
  placeholder?: string;
  disabled?: boolean;
  /** Shown in the list when there are no options at all. */
  emptyText?: string;
  /** A filter box above the list. Defaults to on when there are more than eight options. */
  searchable?: boolean;
  /** How many names the field shows before "+N". */
  summaryMax?: number;
  /**
   * Show what is chosen as removable chips under the field, and a count in
   * the field itself. For a field where the choices are the point (a policy's
   * departments), as opposed to a table cell where there is no room for them.
   */
  chips?: boolean;
  /** How many chips are shown before the rest fold into "+N more". */
  chipMax?: number;
  className?: string;
  /** The id of text that describes the field (a hint or an error). */
  describedBy?: string;
  invalid?: boolean;
}

interface Placement { top?: number; bottom?: number; left: number; width: number; maxHeight: number }

const LIST_MAX = 360;

export function summarize(names: readonly string[], placeholder: string, max: number): string {
  if (names.length === 0) return placeholder;
  if (names.length <= max) return names.join(', ');
  return `${names.slice(0, max).join(', ')} +${names.length - max}`;
}

export function MultiSelect({
  id, label, options, value, onChange, placeholder = 'None', disabled = false, emptyText = 'Nothing to choose from yet.',
  searchable, summaryMax = 2, className, describedBy, invalid, chips = false, chipMax = 6,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [allChips, setAllChips] = useState(false);
  // What was chosen when the list opened. Those are listed first, and stay
  // where they are while the list is open: an option that jumped to the top
  // as it was ticked would move out from under the pointer.
  const [pinned, setPinned] = useState<readonly string[]>([]);
  const [query, setQuery] = useState('');
  const [place, setPlace] = useState<Placement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const uid = useId();
  const popId = `${id}-list`;
  const searchId = `${uid}-search`;

  const chosen = useMemo(() => options.filter((o) => value.includes(o.id)), [options, value]);
  const chosenNames = useMemo(() => chosen.map((o) => o.name), [chosen]);
  const filter = searchable ?? options.length > 8;
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matching = q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options;
    if (pinned.length === 0) return matching;
    return [...matching.filter((o) => pinned.includes(o.id)), ...matching.filter((o) => !pinned.includes(o.id))];
  }, [options, query, pinned]);

  // Where the list goes: under the button, or above it when that has more room.
  function measure() {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const vh = window.innerHeight || 800;
    const vw = window.innerWidth || 1200;
    const below = vh - r.bottom - 8;
    const above = r.top - 8;
    const up = below < Math.min(LIST_MAX, 240) && above > below;
    const width = Math.min(Math.max(r.width, 280), vw - 16);
    const left = Math.max(8, Math.min(r.left, vw - width - 8));
    setPlace(up
      ? { bottom: vh - r.top + 4, left, width, maxHeight: Math.min(LIST_MAX, above) }
      : { top: r.bottom + 4, left, width, maxHeight: Math.min(LIST_MAX, Math.max(below, 160)) });
  }

  useLayoutEffect(() => {
    if (!open) return;
    measure();
    const onMove = () => measure();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => { window.removeEventListener('resize', onMove); window.removeEventListener('scroll', onMove, true); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Close on a click elsewhere or on Escape; give focus back to the button.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); triggerRef.current?.focus(); }
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('pointerdown', onDown, true); document.removeEventListener('keydown', onKey, true); };
  }, [open]);

  // On opening, the filter box or the first option takes focus.
  useEffect(() => {
    if (!open) { setQuery(''); return; }
    const first = popRef.current?.querySelector<HTMLElement>(filter ? 'input[type="text"]' : 'input[type="checkbox"]');
    first?.focus();
  }, [open, filter]);

  function toggle(optionId: string) {
    onChange(value.includes(optionId) ? value.filter((v) => v !== optionId) : [...value, optionId]);
  }

  function onListKey(e: KeyboardEvent<HTMLElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const boxes = Array.from(popRef.current?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]') ?? []);
    if (boxes.length === 0) return;
    const i = boxes.indexOf(document.activeElement as HTMLInputElement);
    const next = e.key === 'ArrowDown' ? Math.min(i + 1, boxes.length - 1) : Math.max(i - 1, 0);
    boxes[next]?.focus();
    e.preventDefault();
  }

  function show() { setPinned(value); setOpen(true); }

  function onTriggerKey(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); show(); }
  }

  function close() { setOpen(false); triggerRef.current?.focus(); }

  const list = open && (
    <div
      ref={popRef}
      id={popId}
      role="group"
      aria-label={label}
      className={styles.pop}
      style={place ? { top: place.top, bottom: place.bottom, left: place.left, width: place.width, maxHeight: place.maxHeight } : undefined}
      onKeyDown={onListKey}
      onBlur={(e) => {
        // Tabbing out of the list closes it; moving within it does not.
        const to = e.relatedTarget as Node | null;
        if (to && (popRef.current?.contains(to) || triggerRef.current?.contains(to))) return;
        if (to) setOpen(false);
      }}
    >
      {filter && (
        <div className={styles.search}>
          <label className="vh" htmlFor={searchId}>Filter {label.toLowerCase()}</label>
          <input id={searchId} type="text" className={styles.searchInput} maxLength={FILTER_MAX} placeholder="Type to filter" autoComplete="off" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
      )}
      <ul className={styles.list}>
        {options.length === 0 && <li className={styles.empty}>{emptyText}</li>}
        {options.length > 0 && shown.length === 0 && <li className={styles.empty}>Nothing matches.</li>}
        {shown.map((o) => (
          <li key={o.id}>
            <label className={styles.option}>
              <input type="checkbox" aria-label={o.name} checked={value.includes(o.id)} onChange={() => toggle(o.id)} disabled={disabled} />
              <span className={styles.optionText}>
                <span>{o.name}</span>
                {o.hint && <span className={styles.hint}>{o.hint}</span>}
              </span>
            </label>
          </li>
        ))}
      </ul>
      <div className={styles.footer}>
        <span>
          {value.length === 0 ? 'None selected' : `${value.length} selected`}
          {value.length > 0 && <>{' · '}<button type="button" className={styles.linkBtn} onClick={() => onChange([])}>Clear all</button></>}
        </span>
        <button type="button" className={styles.done} onClick={close}>Done</button>
      </div>
    </div>
  );

  return (
    <div className={`${styles.root} ${className ?? ''}`}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        className={styles.trigger}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={onTriggerKey}
      >
        <span className={`${styles.summary} ${chosenNames.length === 0 ? styles.placeholder : ''}`}>
          {chips ? (chosen.length === 0 ? placeholder : `${chosen.length} selected`) : summarize(chosenNames, placeholder, summaryMax)}
        </span>
        <svg className={styles.chev} width="14" height="14" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {chips && chosen.length > 0 && (
        <ul className={styles.chips} aria-label={`Selected ${label.toLowerCase()}`}>
          {(allChips ? chosen : chosen.slice(0, chipMax)).map((o) => (
            <li key={o.id} className={styles.chip}>
              <span className={styles.chipText} title={o.name}>{o.name}</span>
              <button type="button" className={styles.chipRemove} aria-label={`Remove ${o.name}`} disabled={disabled} onClick={() => { toggle(o.id); triggerRef.current?.focus(); }}>
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false"><path d="M1.5 1.5l7 7M8.5 1.5l-7 7" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
              </button>
            </li>
          ))}
          {chosen.length > chipMax && (
            <li>
              <button type="button" className={styles.chipMore} aria-expanded={allChips} onClick={() => setAllChips((a) => !a)}>
                {allChips ? 'Show fewer' : `+${chosen.length - chipMax} more`}
              </button>
            </li>
          )}
        </ul>
      )}
      {list && typeof document !== 'undefined' ? createPortal(list, document.body) : null}
    </div>
  );
}
